import {
  Alert,
  Box,
  Card,
  CardContent,
  Container,
  Divider,
  Stack,
  Typography,
} from "@mui/material";
import AppButton from "../../components/button/AppButton";
import AppTextInput from "../../components/inputs/AppTextInput";
import { AppColors } from "../../constants/AppColors";
import { useAdminLoginScreen } from "./hooks/useAdminLoginScreen";

const AdminLoginScreen = () => {
  const {
    email,
    setEmail,
    password,
    setPassword,
    code,
    setCode,
    loading,
    error,
    getFieldError,
    handleSubmit,
    twoFactorMode,
    setupSecret,
    setupOtpAuthUri,
    recoveryCodes,
    handleContinueAfterSavingRecoveryCodes,
  } = useAdminLoginScreen();

  const isSetupMode = twoFactorMode === "setup";
  const isChallengeMode = twoFactorMode === "challenge";
  const hasRecoveryCodes = recoveryCodes.length > 0;

  const title = isSetupMode
    ? "Set up admin 2FA"
    : isChallengeMode
      ? "Enter 2FA code"
      : "RentPilot Admin";

  const description = isSetupMode
    ? "Open Proton Authenticator, add a new account manually, paste the setup key below, then enter the 6-digit code."
    : isChallengeMode
      ? "Enter the 6-digit code from Proton Authenticator, or use one recovery code."
      : "Internal login for managing accounts, subscriptions and logs.";

  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: "background.default",
        display: "flex",
        alignItems: "center",
        py: 4,
      }}
    >
      <Container maxWidth="sm">
        <Card>
          <CardContent sx={{ p: { xs: 3, md: 5 } }}>
            <Stack spacing={3}>
              <Stack
                spacing={1}
                sx={{
                  alignItems: "center",
                }}
              >
                <Box
                  sx={{
                    width: 62,
                    height: 62,
                    borderRadius: "22px",
                    bgcolor: AppColors.primary,
                    color: AppColors.textOnPrimary,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 22,
                    fontWeight: 900,
                  }}
                >
                  RP
                </Box>

                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 900,
                    textAlign: "center",
                  }}
                >
                  {title}
                </Typography>

                <Typography
                  color="text.secondary"
                  sx={{
                    textAlign: "center",
                  }}
                >
                  {description}
                </Typography>
              </Stack>

              {error ? <Alert severity="error">{error}</Alert> : null}

              {hasRecoveryCodes ? (
                <Stack spacing={2}>
                  <Alert severity="warning">
                    Save these recovery codes now. You will not see them again.
                  </Alert>

                  <Box
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      bgcolor: "background.default",
                      border: "1px solid",
                      borderColor: "divider",
                      fontFamily: "monospace",
                      whiteSpace: "pre-wrap",
                      wordBreak: "break-word",
                    }}
                  >
                    {recoveryCodes.join("\n")}
                  </Box>

                  <AppButton
                    onClick={handleContinueAfterSavingRecoveryCodes}
                    fullWidth
                  >
                    I saved the recovery codes
                  </AppButton>
                </Stack>
              ) : (
                <Stack
                  component="form"
                  spacing={2}
                  onSubmit={(event) => {
                    event.preventDefault();
                    handleSubmit();
                  }}
                >
                  {!isSetupMode && !isChallengeMode ? (
                    <>
                      <AppTextInput
                        label="Email"
                        type="email"
                        value={email}
                        onChange={setEmail}
                        error={getFieldError("email")}
                        required
                      />

                      <AppTextInput
                        label="Password"
                        type="password"
                        value={password}
                        onChange={setPassword}
                        error={getFieldError("password")}
                        required
                      />
                    </>
                  ) : null}

                  {isSetupMode ? (
                    <>
                      <Alert severity="info">
                        Proton Authenticator works with this setup key. Add a
                        new TOTP account manually and paste this key.
                      </Alert>

                      <Box
                        sx={{
                          p: 2,
                          borderRadius: 2,
                          bgcolor: "background.default",
                          border: "1px solid",
                          borderColor: "divider",
                          fontFamily: "monospace",
                          wordBreak: "break-all",
                        }}
                      >
                        {setupSecret || "Loading setup key..."}
                      </Box>

                      {setupOtpAuthUri ? (
                        <Box
                          sx={{
                            p: 2,
                            borderRadius: 2,
                            bgcolor: "background.default",
                            border: "1px solid",
                            borderColor: "divider",
                            fontFamily: "monospace",
                            fontSize: 12,
                            wordBreak: "break-all",
                          }}
                        >
                          {setupOtpAuthUri}
                        </Box>
                      ) : null}

                      <Divider />
                    </>
                  ) : null}

                  {isSetupMode || isChallengeMode ? (
                    <AppTextInput
                      label={
                        isChallengeMode
                          ? "2FA code or recovery code"
                          : "6-digit 2FA code"
                      }
                      value={code}
                      onChange={setCode}
                      error={getFieldError("code")}
                      required
                    />
                  ) : null}

                  <AppButton type="submit" loading={loading} fullWidth>
                    {isSetupMode
                      ? "Enable 2FA"
                      : isChallengeMode
                        ? "Verify"
                        : "Login"}
                  </AppButton>
                </Stack>
              )}
            </Stack>
          </CardContent>
        </Card>
      </Container>
    </Box>
  );
};

export default AdminLoginScreen;
